import Chip from '@mui/material/Chip'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { noop } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useLazyGetSuspensionProrationQuery, useUpdateSuspensionProrationMutation } from 'api/accounts/accountBillingSettings'
import { useLazyGetSuspensionProrationVersionsQuery } from 'api/entity-version/entityVersionCrud'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import InheritBillingProfileCheckbox from './common/InheritBillingProfileCheckbox'
import OnOffRadioGroup from './common/OnOffRadioGroup'
import SettingsHistoryAccordion from './common/SettingsHistoryAccordion'
import useVersionsState from './common/useVersionsState'

const FORM_DEFAULTS = {
  prorationEnabled: 'false',
  inheritFromBillingProfile: true,
}

const SuspensionProrationDialog = ({ open = false, onClose = noop, accountId }) => {
  const [getSuspensionProration, { data, isFetching }] = useLazyGetSuspensionProrationQuery()
  const [updateSuspensionProration, { isLoading: isUpdating }] = useUpdateSuspensionProrationMutation()
  const [getVersions, { data: versionsData, isLoading: isVersionsLoading, isFetching: isVersionsFetching, isSuccess: isVersionsSuccess }] =
    useLazyGetSuspensionProrationVersionsQuery()

  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: FORM_DEFAULTS })

  const prorationEnabled = watch('prorationEnabled')
  const inheritFromBillingProfile = watch('inheritFromBillingProfile')
  const billingProfile = get(data, 'billingProfile', null)
  const billingProfileName = get(billingProfile, 'name', '')

  const { versions, hasVersions, versionsLoading } = useVersionsState({
    versionsData,
    isVersionsLoading,
    isFetching: isVersionsFetching,
    isVersionsSuccess,
  })

  useEffect(() => {
    if (open && accountId) {
      getSuspensionProration({ accountId }).unwrap().catch(handleError)
      getVersions({ accountId }).unwrap().catch(handleError)
    }
  }, [open, accountId, getSuspensionProration, getVersions])

  useEffect(() => {
    if (open && data) {
      const inheriting = get(data, 'inheritFromBillingProfile', true)
      const bp = get(data, 'billingProfile')
      const source = inheriting && bp ? bp : data
      reset({
        prorationEnabled: String(get(source, 'prorationEnabled', false)),
        inheritFromBillingProfile: inheriting,
      })
    }
  }, [open, data, reset])

  const handleProrationChange = useCallback(
    event => {
      setValue('prorationEnabled', event.target.value, { shouldDirty: true })
    },
    [setValue]
  )

  const handleInheritChange = useCallback(
    event => {
      const { checked } = event.target
      setValue('inheritFromBillingProfile', checked, { shouldDirty: true })
      if (checked && billingProfile) {
        setValue('prorationEnabled', String(get(billingProfile, 'prorationEnabled', false)), { shouldDirty: true })
      }
    },
    [setValue, billingProfile]
  )

  const getVersionChangeLines = useCallback(version => {
    const lines = []
    if ('prorationEnabled' in version) {
      lines.push(`Proration: ${version.prorationEnabled ? T.ON : T.OFF}`)
    }
    if ('inheritFromBillingProfile' in version) {
      lines.push(`Inherit from profile: ${version.inheritFromBillingProfile ? T.ON : T.OFF}`)
    }
    if (lines.length === 0) {
      lines.push('Settings updated')
    }
    return lines
  }, [])

  const handleUpdate = useCallback(() => {
    const values = getValues()
    updateSuspensionProration({
      accountId,
      prorationEnabled: values.prorationEnabled === 'true',
      inheritFromBillingProfile: values.inheritFromBillingProfile,
    })
      .unwrap()
      .then(() => {
        toast.success(T.SETTINGS_SAVED_SUCCESSFULLY)
        onClose()
      })
      .catch(handleError)
  }, [accountId, getValues, updateSuspensionProration, onClose])

  return (
    <HHBaseDialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <HHDialogTitle title={T.SUSPENSION_PRORATION} onClose={onClose} />
      <DialogContent>
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <Typography sx={{ mb: 2 }} variant="body1">
            {T.ACCOUNT_SUSPENSION_PRORATION_SETTINGS_TOOLTIP}
          </Typography>
          <OnOffRadioGroup
            disabled={inheritFromBillingProfile}
            formControlSx={{ display: 'block', mb: 2 }}
            labelSx={{ my: 1 }}
            offDescription={T.BILL_THEM}
            offLabel={
              <Stack alignItems="center" direction="row" gap={0.5}>
                <Typography fontWeight="bold" textTransform="uppercase" variant="h6">
                  {T.OFF}
                </Typography>
                <Chip
                  label={T.DEFAULT}
                  size="small"
                  sx={{
                    bgcolor: '#e3f2fd',
                    color: '#1565c0',
                    fontWeight: 400,
                    fontSize: 11,
                    height: 22,
                    borderRadius: '2px',
                    border: '1px solid #90caf9',
                    textTransform: 'lowercase',
                  }}
                />
              </Stack>
            }
            offTooltip={T.SUSPENSION_PRORATION_OFF_TOOLTIP}
            value={prorationEnabled}
            onChange={handleProrationChange}
            onDescription={T.PRORATE_DO_NOT_BILL_DURING_SUSPENSION}
            onLabel={T.ON}
            onTooltip={T.SUSPENSION_PRORATION_ON_TOOLTIP}
          />
          <Divider sx={{ my: 2 }} />
          <InheritBillingProfileCheckbox
            billingProfileName={billingProfileName}
            checked={inheritFromBillingProfile}
            onChange={handleInheritChange}
          >
            {billingProfile && (
              <Typography color="text.secondary" variant="body2">
                {T.PRORATION}
                {': '}
                <strong>{billingProfile.prorationEnabled ? T.ON : T.OFF}</strong>
              </Typography>
            )}
          </InheritBillingProfileCheckbox>
        </Paper>
        <SettingsHistoryAccordion
          getVersionChangeLines={getVersionChangeLines}
          hasVersions={hasVersions}
          versions={versions}
          versionsLoading={versionsLoading}
        />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton disabled={!isDirty} label={T.SAVE} loading={isUpdating || isFetching} onClick={handleUpdate} />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

SuspensionProrationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  accountId: PropTypes.string,
}

export default SuspensionProrationDialog
